/**
 * SDR EOD Form - Public submission form
 * URL: /sdr-eod
 */

import { useState } from "react";
import {
  EODFormLayout,
  FormField,
  TextInput,
  TextArea,
  Select,
  RatingInput,
  SubmitButton,
  SuccessMessage,
  ErrorMessage,
} from "./EODFormLayout";

const SDRS = [
  { value: "Raffi", label: "Raffi" },
  { value: "Koehn", label: "Koehn" },
  { value: "Dylan", label: "Dylan" },
  { value: "Jorge", label: "Jorge" },
  { value: "Andy", label: "Andy" },
  { value: "Noah", label: "Noah" },
  { value: "Josh", label: "Josh" },
  { value: "Ellie", label: "Ellie" },
  { value: "Fred", label: "Fred" },
  { value: "Ava", label: "Ava" },
  { value: "Tristen", label: "Tristen" },
  { value: "Harrison", label: "Harrison" },
  { value: "Colin", label: "Colin" },
  { value: "Kaan", label: "Kaan" },
];

const DIAL_TYPE_OPTIONS = [
  { value: "Applications", label: "Applications" },
  { value: "Opt-ins", label: "Opt-ins" },
  { value: "Both", label: "Both" },
];

const CASH_BLOCK_OPTIONS = [
  { value: "Yes", label: "Yes I dialed my entire cashblock and it was filled with cash producing activities" },
  { value: "No", label: "No I didn't use my cash block time for cash producing activities" },
];

const CALENDAR_ADHERENCE_OPTIONS = [
  { value: "1-3", label: "1-3 - Poorly" },
  { value: "4-6", label: "4-6 - Okay" },
  { value: "7-8", label: "7-8 - Good" },
  { value: "9-10", label: "9-10 - Excellent" },
];

interface FormData {
  name: string;
  dialType: string;
  eodDate: string;
  dials: string;
  connected: string;
  leadsBooked: string;
  qualifiedLeads: string;
  leadflowConfidence: number | undefined;
  wentWell: string;
  couldImprove: string;
  cashBlockEffective: string;
  calendarAdherence: string;
  cashUpdated: string;
  needHelp: string;
}

function getTodayDate(): string {
  return new Date().toISOString().split("T")[0] ?? "";
}

const initialFormData: FormData = {
  name: "",
  dialType: "",
  eodDate: getTodayDate(),
  dials: "",
  connected: "",
  leadsBooked: "",
  qualifiedLeads: "",
  leadflowConfidence: undefined,
  wentWell: "",
  couldImprove: "",
  cashBlockEffective: "",
  calendarAdherence: "",
  cashUpdated: "",
  needHelp: "",
};

export function SdrEodForm() {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch("/api/eod/submit/sdr", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          dialType: formData.dialType,
          eodDate: formData.eodDate,
          dials: parseInt(formData.dials) || 0,
          connected: parseInt(formData.connected) || 0,
          leadsBooked: parseInt(formData.leadsBooked) || 0,
          qualifiedLeads: parseInt(formData.qualifiedLeads) || 0,
          leadflowConfidence: formData.leadflowConfidence,
          wentWell: formData.wentWell,
          couldImprove: formData.couldImprove,
          cashBlockEffective: formData.cashBlockEffective,
          calendarAdherence: formData.calendarAdherence,
          cashUpdated: formData.cashUpdated,
          needHelp: formData.needHelp,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || "Failed to submit EOD");
      }

      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData(initialFormData);
    setSubmitted(false);
    setError(null);
  };

  if (submitted) {
    return (
      <EODFormLayout title="SDR EOD Report" subtitle="Daily report for SDRs">
        <SuccessMessage onReset={handleReset} />
      </EODFormLayout>
    );
  }

  return (
    <EODFormLayout title="SDR EOD Report" subtitle="Fill out your daily setting report">
      {error && <ErrorMessage message={error} onRetry={() => setError(null)} />}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label="Who is filling this form out?" required>
            <Select
              value={formData.name}
              onChange={(v) => updateField("name", v)}
              options={SDRS}
              placeholder="Select your name"
              required
            />
          </FormField>

          <FormField label="What did you dial today?" required>
            <Select
              value={formData.dialType}
              onChange={(v) => updateField("dialType", v)}
              options={DIAL_TYPE_OPTIONS}
              placeholder="Select dial type"
              required
            />
          </FormField>
        </div>

        <FormField label="What date is this EOD for?" required>
          <TextInput
            type="date"
            value={formData.eodDate}
            onChange={(v) => updateField("eodDate", v)}
            required
          />
        </FormField>

        {/* Dial Metrics */}
        <div className="border-t border-white/10 pt-6">
          <h3 className="text-lg font-semibold text-white mb-4">Dial Metrics</h3>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="How many dials did you do today?" required hint="Just put number">
              <TextInput
                type="number"
                value={formData.dials}
                onChange={(v) => updateField("dials", v)}
                placeholder="0"
                min={0}
                required
              />
            </FormField>

            <FormField label="How many connected?" required hint="Just put number">
              <TextInput
                type="number"
                value={formData.connected}
                onChange={(v) => updateField("connected", v)}
                placeholder="0"
                min={0}
                required
              />
            </FormField>

            <FormField label="How many leads did you book?" required hint="Just put number">
              <TextInput
                type="number"
                value={formData.leadsBooked}
                onChange={(v) => updateField("leadsBooked", v)}
                placeholder="0"
                min={0}
                required
              />
            </FormField>

            <FormField label='How many "qualified" leads did you talk to?' required hint="This number should be equal or higher than the amount of people you booked">
              <TextInput
                type="number"
                value={formData.qualifiedLeads}
                onChange={(v) => updateField("qualifiedLeads", v)}
                placeholder="0"
                min={0}
                required
              />
            </FormField>
          </div>
        </div>

        {/* Leadflow Confidence */}
        <div className="border-t border-white/10 pt-6">
          <FormField
            label="What is your confidence level with leadflow quality TODAY ONLY?"
            hint="0 = horrible, 10 = solid. ONLY PUT IF YOU HAVE AN HONEST ANSWER. If no one answered, don't answer this."
          >
            <RatingInput
              value={formData.leadflowConfidence}
              onChange={(v) => updateField("leadflowConfidence", v)}
            />
          </FormField>
        </div>

        {/* Feedback Section */}
        <div className="border-t border-white/10 pt-6">
          <h3 className="text-lg font-semibold text-white mb-4">Daily Feedback</h3>
          <div className="space-y-4">
            <FormField label="What went well today?">
              <TextArea
                value={formData.wentWell}
                onChange={(v) => updateField("wentWell", v)}
                placeholder="Describe what worked..."
                rows={3}
              />
            </FormField>

            <FormField label="What could have went better?" required>
              <TextArea
                value={formData.couldImprove}
                onChange={(v) => updateField("couldImprove", v)}
                placeholder="Areas for improvement..."
                rows={3}
                required
              />
            </FormField>
          </div>
        </div>

        {/* Operations Questions */}
        <div className="border-t border-white/10 pt-6">
          <h3 className="text-lg font-semibold text-white mb-4">Operations</h3>
          <div className="space-y-4">
            <FormField label="Did you use your cash block time effectively?" required hint="Dialing no shows/follow ups, texting closers to collect deposits/MRRs, outbound DMs, etc.">
              <Select
                value={formData.cashBlockEffective}
                onChange={(v) => updateField("cashBlockEffective", v)}
                options={CASH_BLOCK_OPTIONS}
                placeholder="Select an option"
                required
              />
            </FormField>

            <FormField label="How well did you stick to your calendar today?" required>
              <Select
                value={formData.calendarAdherence}
                onChange={(v) => updateField("calendarAdherence", v)}
                options={CALENDAR_ADHERENCE_OPTIONS}
                placeholder="Select a rating"
                required
              />
            </FormField>

            <FormField label="Is your cash fully updated from closers in the dashboard?" required hint="Either new cash or MRR. If missing cash, explain what is missing.">
              <TextArea
                value={formData.cashUpdated}
                onChange={(v) => updateField("cashUpdated", v)}
                placeholder="Yes, or explain what's missing..."
                rows={2}
                required
              />
            </FormField>

            <FormField label="Do you need help with something from Jack to perform better?" required>
              <TextArea
                value={formData.needHelp}
                onChange={(v) => updateField("needHelp", v)}
                placeholder="Describe what you need help with..."
                rows={2}
                required
              />
            </FormField>
          </div>
        </div>

        {/* Submit */}
        <div className="pt-4">
          <SubmitButton loading={loading} />
        </div>
      </form>
    </EODFormLayout>
  );
}

export default SdrEodForm;
